/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { TRUSTED_ORACLE_PROGRAM_ADDRESS } from '../programs';

export const INITIALIZE_ORACLE_DISCRIMINATOR = new Uint8Array([
  144, 223, 131, 120, 196, 253, 181, 99,
]);

export function getInitializeOracleDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_ORACLE_DISCRIMINATOR,
  );
}

export type InitializeOracleInstruction<
  TProgram extends string = typeof TRUSTED_ORACLE_PROGRAM_ADDRESS,
  TAccountOracleAuthority extends string | AccountMeta<string> = string,
  TAccountOracleState extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    '11111111111111111111111111111111',
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountOracleAuthority extends string
        ? WritableSignerAccount<TAccountOracleAuthority> &
            AccountSignerMeta<TAccountOracleAuthority>
        : TAccountOracleAuthority,
      TAccountOracleState extends string
        ? WritableAccount<TAccountOracleState>
        : TAccountOracleState,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeOracleInstructionData = {
  discriminator: ReadonlyUint8Array;
  /** Nonce for PDA derivation (allows multiple oracles per authority) */
  nonce: bigint;
  /** Optional quote mint for validation (use Pubkey::default() if not needed) */
  quoteMint: Address;
};

export type InitializeOracleInstructionDataArgs = {
  /** Nonce for PDA derivation (allows multiple oracles per authority) */
  nonce: number | bigint;
  /** Optional quote mint for validation (use Pubkey::default() if not needed) */
  quoteMint: Address;
};

export function getInitializeOracleInstructionDataEncoder(): FixedSizeEncoder<InitializeOracleInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['nonce', getU64Encoder()],
      ['quoteMint', getAddressEncoder()],
    ]),
    (value) => ({ ...value, discriminator: INITIALIZE_ORACLE_DISCRIMINATOR }),
  );
}

export function getInitializeOracleInstructionDataDecoder(): FixedSizeDecoder<InitializeOracleInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['nonce', getU64Decoder()],
    ['quoteMint', getAddressDecoder()],
  ]);
}

export function getInitializeOracleInstructionDataCodec(): FixedSizeCodec<
  InitializeOracleInstructionDataArgs,
  InitializeOracleInstructionData
> {
  return combineCodec(
    getInitializeOracleInstructionDataEncoder(),
    getInitializeOracleInstructionDataDecoder(),
  );
}

export type InitializeOracleInput<
  TAccountOracleAuthority extends string = string,
  TAccountOracleState extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  /** The authority that will be able to finalize this oracle */
  oracleAuthority: TransactionSigner<TAccountOracleAuthority>;
  oracleState: Address<TAccountOracleState>;
  systemProgram?: Address<TAccountSystemProgram>;
  nonce: InitializeOracleInstructionDataArgs['nonce'];
  quoteMint: InitializeOracleInstructionDataArgs['quoteMint'];
};

export function getInitializeOracleInstruction<
  TAccountOracleAuthority extends string,
  TAccountOracleState extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address = typeof TRUSTED_ORACLE_PROGRAM_ADDRESS,
>(
  input: InitializeOracleInput<
    TAccountOracleAuthority,
    TAccountOracleState,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeOracleInstruction<
  TProgramAddress,
  TAccountOracleAuthority,
  TAccountOracleState,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? TRUSTED_ORACLE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    oracleAuthority: { value: input.oracleAuthority ?? null, isWritable: true },
    oracleState: { value: input.oracleState ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      '11111111111111111111111111111111' as Address<'11111111111111111111111111111111'>;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('oracleAuthority', accounts.oracleAuthority),
      getAccountMeta('oracleState', accounts.oracleState),
      getAccountMeta('systemProgram', accounts.systemProgram),
    ],
    data: getInitializeOracleInstructionDataEncoder().encode(
      args as InitializeOracleInstructionDataArgs,
    ),
    programAddress,
  } as InitializeOracleInstruction<
    TProgramAddress,
    TAccountOracleAuthority,
    TAccountOracleState,
    TAccountSystemProgram
  >);
}

export type ParsedInitializeOracleInstruction<
  TProgram extends string = typeof TRUSTED_ORACLE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** The authority that will be able to finalize this oracle */
    oracleAuthority: TAccountMetas[0];
    oracleState: TAccountMetas[1];
    systemProgram: TAccountMetas[2];
  };
  data: InitializeOracleInstructionData;
};

export function parseInitializeOracleInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeOracleInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 3,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      oracleAuthority: getNextAccount(),
      oracleState: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeOracleInstructionDataDecoder().decode(instruction.data),
  };
}
